import { V2MicrogridOverviewGetNewEnergyGenerationTrendPostResponse } from '@maxtropy/intelligent-dispath-apis-v2';
import { ModeType } from '.';
import dayjs from 'dayjs';
import { keepTwoNull } from '@/pages/IndustrialGreenMicBigScreen/utils';
import * as echarts from 'echarts';

const areaColors = [
  new echarts.graphic.LinearGradient(0, 0, 0, 1, [
    { offset: 0, color: '#4A90E220' },
    { offset: 1, color: '#4A90E210' },
  ]),
  new echarts.graphic.LinearGradient(0, 0, 0, 1, [
    { offset: 0, color: '#FFCB4720' },
    { offset: 1, color: '#FFCB4710' },
  ]),
];

export const getChartOption = (mode: ModeType, data?: V2MicrogridOverviewGetNewEnergyGenerationTrendPostResponse) => {
  // 实际值
  let actualData =
    mode === ModeType.PV
      ? (data?.pvTrend?.trendData?.actData ?? []).map(item => [dayjs(item.time).valueOf(), keepTwoNull(item.value)])
      : (data?.wpTrend?.actData ?? []).map(item => [dayjs(item.time).valueOf(), keepTwoNull(item.value)]);

  // 理论值
  let theoryData =
    mode === ModeType.PV
      ? (data?.pvTrend?.trendData?.theoryData ?? []).map(item => [dayjs(item.time).valueOf(), keepTwoNull(item.value)])
      : (data?.wpTrend?.theoryData ?? []).map(item => [dayjs(item.time).valueOf(), keepTwoNull(item.value)]);

  // 预测值
  let predictiveData =
    mode === ModeType.PV
      ? (data?.pvTrend?.trendData?.predictData ?? []).map(item => [dayjs(item.time).valueOf(), keepTwoNull(item.value)])
      : (data?.wpTrend?.predictData ?? []).map(item => [dayjs(item.time).valueOf(), keepTwoNull(item.value)]);

  const unit = 'kWh';

  return {
    color:
      mode === ModeType.PV
        ? ['rgba(22, 221, 142, 1)', '#2D8DFF', 'rgba(255, 203, 71, 1)']
        : ['rgba(22, 221, 142, 1)', 'rgba(255, 203, 71, 1)'],
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(0,0,0,0.8)',
      borderColor: 'transparent',
      textStyle: {
        color: 'rgba(255,255,255,0.85)',
      },
      formatter: (items: any) => {
        let { axisValue } = items[0];
        let firstStr = dayjs(axisValue).format('YYYY-MM-DD');
        let otherStr = '';
        let str = '';

        items.forEach((item: any) => {
          const { seriesName, data, marker } = item;
          otherStr += `<div style="margin-top:8px"><span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;">${marker}</span>${seriesName} : &nbsp;${
            data[1] || data[1] === 0 ? data[1] : '-'
          }${unit}</div>`;
          str = firstStr + otherStr;
        });

        return str;
      },
    },
    grid: {
      left: 10,
      right: 20,
      top: 30,
      bottom: 10,
      containLabel: true,
    },
    legend: {
      right: 10,
      itemGap: 18,
      itemWidth: 16,
      itemHeight: 4,
      textStyle: {
        color: 'rgba(255,255,255,0.85)',
      },
      inactiveColor: 'rgba(255,255,255,0.45)',
      data:
        mode === ModeType.PV
          ? [
              {
                name: '实际发电量',
                icon: 'rect',
              },
              {
                name: '理论发电量',
                icon: 'path://M54 468h261v90H54zM378 468h261v90H378zM702 468h261v90H702z',
              },
              {
                name: '预测值',
                icon: 'path://M54 468h261v90H54zM378 468h261v90H378zM702 468h261v90H702z',
              },
            ]
          : [
              {
                name: '实际发电量',
                icon: 'rect',
              },
              {
                name: '预测值',
                icon: 'path://M54 468h261v90H54zM378 468h261v90H378zM702 468h261v90H702z',
              },
            ],
    },

    xAxis: [
      {
        type: 'time',
        axisTick: {
          show: false,
        },
        axisLabel: {
          textStyle: {
            color: 'rgba(255,255,255,.85)', //坐标的字体颜色
          },
          formatter: (value: string) => {
            return dayjs(value).format('MM-DD');
          },
          fontSize: 10,
          hideOverlap: true,
        },
        axisPointer: {
          type: 'shadow',
        },
        axisLine: {
          show: true, // 是否显示轴线
          lineStyle: {
            color: 'rgba(255,255,255,0.3)',
          },
        },
      },
    ],
    yAxis: [
      {
        type: 'value',
        name: unit,
        nameTextStyle: {
          color: 'rgba(255,255,255,0.85)',
        },
        axisLabel: {
          formatter: '{value}',
          textStyle: {
            color: 'rgba(255,255,255,.85)', //坐标的字体颜色
          },
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: 'rgba(255,255,255,0.3)',
          },
        },
      },
    ],
    series:
      mode === ModeType.PV
        ? [
            {
              name: '实际发电量',
              type: 'line',
              data: actualData,
              connectNulls: true,
              smooth: true,
              symbol: 'circle',
              symbolSize: 6,
              lineStyle: {
                width: 2,
                type: 'solid', //'dotted'点型虚线 'solid'实线 'dashed'线性虚线
              },
              areaStyle: {
                color: areaColors[0],
              },
            },
            {
              name: '理论发电量',
              type: 'line',
              data: theoryData,
              connectNulls: true,
              smooth: true,
              symbol: 'circle',
              symbolSize: 6,
              lineStyle: {
                width: 2,
                type: 'dashed', //'dotted'点型虚线 'solid'实线 'dashed'线性虚线
              },
            },
            {
              name: '预测值',
              type: 'line',
              data: predictiveData,
              connectNulls: true,
              smooth: true,
              symbol: 'circle',
              symbolSize: 6,
              lineStyle: {
                width: 2,
                type: 'dashed', //'dotted'点型虚线 'solid'实线 'dashed'线性虚线
              },
              areaStyle: {
                color: areaColors[1],
              },
            },
          ]
        : [
            {
              name: '实际发电量',
              type: 'line',
              data: actualData,
              connectNulls: true,
              smooth: true,
              symbol: 'circle',
              symbolSize: 6,
              lineStyle: {
                width: 2,
                type: 'solid', //'dotted'点型虚线 'solid'实线 'dashed'线性虚线
              },
              areaStyle: {
                color: areaColors[0],
              },
            },

            {
              name: '预测值',
              type: 'line',
              data: predictiveData,
              connectNulls: true,
              smooth: true,
              symbol: 'circle',
              symbolSize: 6,
              lineStyle: {
                width: 2,
                type: 'dashed', //'dotted'点型虚线 'solid'实线 'dashed'线性虚线
              },
              areaStyle: {
                color: areaColors[1],
              },
            },
          ],
  };
};
